import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AUTH } from "@/constants/testIds";
import FallingLeaves from "@/components/common/FallingLeaves";
import { Leaf, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, setSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [stage, setStage] = useState("credentials"); // credentials | totp_setup | totp_verify
  const [busy, setBusy] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submitCreds = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data.stage === "totp_setup_required") {
        localStorage.setItem("od_token", r.data.token);
        localStorage.setItem("od_stage", "password_ok");
        // Fetch setup data
        const s = await api.post("/auth/totp/setup");
        setSetupData(s.data);
        setStage("totp_setup");
      } else {
        localStorage.setItem("od_token", r.data.token);
        localStorage.setItem("od_stage", "password_ok");
        setStage("totp_verify");
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const endpoint = stage === "totp_setup" ? "/auth/totp/confirm" : "/auth/totp/verify";
    try {
      const r = await api.post(endpoint, { token: localStorage.getItem("od_token"), code: totp });
      setSession({ token: r.data.token, stage: "full", user: r.data.user });
      toast.success("Signed in");
      navigate("/", { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <FallingLeaves count={22} />
      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#0A84FF,#5B8C5A)" }}>
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-outfit font-semibold text-lg text-slate-900">Outstanding Dime</div>
              <div className="text-xs text-slate-500">Secure analytics dashboard</div>
            </div>
          </div>

          {stage === "credentials" && (
            <form onSubmit={submitCreds} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoFocus
                  required
                  className="glass-input mt-1"
                  data-testid={AUTH.emailInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  className="glass-input mt-1"
                  data-testid={AUTH.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {err && <p className="text-sm text-red-600" data-testid={AUTH.errorMsg}>{err}</p>}
              <Button
                type="submit"
                disabled={busy}
                className="w-full btn-tactile"
                style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}
                data-testid={AUTH.loginBtn}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
              </Button>
            </form>
          )}

          {stage === "totp_setup" && setupData && (
            <form onSubmit={submitTotp} className="space-y-4">
              <div>
                <div className="font-outfit font-semibold text-slate-900">Set up 2FA</div>
                <p className="text-sm text-slate-600 mt-1">
                  Scan this QR in Google Authenticator, Authy, or 1Password. Then enter the 6-digit code.
                </p>
              </div>
              <div className="glass-solid p-4 flex flex-col items-center gap-3">
                <img src={setupData.qr_png_base64} alt="TOTP QR" className="w-40 h-40" />
                <div className="text-[11px] font-mono text-slate-500 break-all text-center">
                  {setupData.secret}
                </div>
              </div>
              <div>
                <Label>Backup codes (save these)</Label>
                <div className="glass-solid p-3 mt-1 font-mono text-xs grid grid-cols-2 gap-1">
                  {setupData.backup_codes.map((c) => <div key={c}>{c}</div>)}
                </div>
              </div>
              <div>
                <Label htmlFor="totp">6-digit code</Label>
                <Input
                  id="totp"
                  className="glass-input mt-1 tracking-widest text-center font-mono"
                  data-testid={AUTH.totpInput}
                  inputMode="numeric"
                  maxLength={6}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                />
              </div>
              {err && <p className="text-sm text-red-600" data-testid={AUTH.errorMsg}>{err}</p>}
              <Button
                type="submit"
                disabled={busy}
                className="w-full btn-tactile"
                style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}
                data-testid={AUTH.totpSetupConfirmBtn}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & sign in"}
              </Button>
            </form>
          )}

          {stage === "totp_verify" && (
            <form onSubmit={submitTotp} className="space-y-4">
              <div>
                <div className="font-outfit font-semibold text-slate-900">Two-factor code</div>
                <p className="text-sm text-slate-600 mt-1">Enter the 6-digit code from your authenticator app.</p>
              </div>
              <Input
                autoFocus
                className="glass-input tracking-widest text-center font-mono text-lg"
                data-testid={AUTH.totpInput}
                inputMode="numeric"
                maxLength={8}
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/[^0-9A-Z]/gi, "").toUpperCase())}
              />
              {err && <p className="text-sm text-red-600" data-testid={AUTH.errorMsg}>{err}</p>}
              <Button
                type="submit"
                disabled={busy}
                className="w-full btn-tactile"
                style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}
                data-testid={AUTH.totpVerifyBtn}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
              </Button>
            </form>
          )}

          <p className="text-[11px] text-slate-500 mt-6 text-center">
            Restricted to whitelisted IP addresses. Access issues? Contact your admin.
          </p>
        </div>
      </div>
    </div>
  );
}
